import styles from "./Field.module.css";
import { Select } from "./Select";

/**
 * A labelled `<select>` in the same shape as the two fields beside it — drawn
 * by `Select`, the app's one look for a native list (Task 11, B-16).
 *
 * A value the options do not carry is still shown as its own option rather
 * than letting the browser fall back to the first one: a parameter the
 * server stored is a fact, and a select that silently shows „Verzinkt" for a
 * stored „V4A" states a falsehood in the one place a person checks.
 */
export function SelectField({
  id,
  label,
  value,
  options,
  disabled = false,
  readOnly = false,
  hint,
  error,
  autoFocusTarget = false,
  labelHidden = false,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  options: readonly { value: string; label: string }[];
  disabled?: boolean;
  /**
   * Shown, not changeable: a `<select>` has no `readonly`, so it is disabled,
   * but keeps the ink of a stated value instead of the greyed „not available"
   * (the handover dialog after a 409, R2-8).
   */
  readOnly?: boolean;
  hint?: string | null;
  error?: string | null;
  /** The field a `Dialog` puts the focus in when it opens (`data-autofocus`). */
  autoFocusTarget?: boolean;
  /**
   * The label is read, not shown — where a heading right above it already
   * says the same word (the Eingang's „Rosttyp“ panel) and a second one in
   * tracked caps would only repeat it.
   */
  labelHidden?: boolean;
  onChange: (value: string) => void;
}) {
  const known = options.some((option) => option.value === value);
  // Fix-Runde 1, M5 — see TextField: both stand, both are read.
  const describedBy = [error ? `${id}-error` : null, hint ? `${id}-hint` : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className={styles.field}>
      <label className={labelHidden ? "visually-hidden" : `label ${styles.fieldLabel}`} htmlFor={id}>
        {label}
      </label>
      <Select
        id={id}
        className={`${styles.input} ${error ? styles.inputBad : ""} ${readOnly ? styles.inputReadOnly : ""}`}
        value={value}
        disabled={disabled || readOnly}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        data-autofocus={autoFocusTarget ? "" : undefined}
        onChange={(event) => onChange(event.target.value)}
      >
        {known ? null : <option value={value}>{value}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
      {error ? (
        <span id={`${id}-error`} className={`${styles.hint} ${styles.hintBad}`}>
          {error}
        </span>
      ) : null}
      {hint ? (
        <span id={`${id}-hint`} className={styles.hint}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}
